import { ScrollArea } from "@sundae/ui-toolkit";
import { EContractVersion } from "@sundaeswap/core";
import cx from "classnames";
import sortBy from "lodash/sortBy";
import { FC, Suspense, lazy, useMemo, useRef } from "react";

import { PoolFragmentFragment } from "../../../gql/generated/stats2.sdk";
import { usePoolsByIdent } from "../../../gql/hooks/pools.query";
import { useAssetsWithBalance } from "../../../hooks/useAssetsWithBalance";
import useIsLPAsset from "../../../hooks/useIsLPAsset";
import { useAppContext } from "../../../stores";
import { ETransactionState } from "../../../types/Transaction.types";
import { useMigrateLiquidityContext } from "../../Dialogs/MigrateLiquidityDialog/context";
import LoadingComponent from "../../LoadingComponent";
import LiquidityPoolMigrationListItem from "./LiquidityPoolMigrationListItem";
import LiquidityPoolMigrationListItemSkeleton from "./LiquidityPoolMigrationListItemSkeleton";

const LazyCreatePoolDialog = lazy(
  () => import(/* webpackChunkName: "CreatePoolDialog" */ "../../Dialogs/CreatePoolDialog")
);

interface ILiquidityPoolMigrationListProps {
  displaySelectPoolsOnly?: boolean;
  txState: ETransactionState;
}

const LiquidityPoolMigrationList: FC<ILiquidityPoolMigrationListProps> = ({
  displaySelectPoolsOnly = false,
  txState,
}) => {
  const {
    derived: { isLoadingV3Pools, v3PoolsByAssetId, selectedPools },
  } = useMigrateLiquidityContext();
  const {
    state: {
      dialogs: { liquidity: liquidityDialogs },
    },
  } = useAppContext();
  const migrationListRef = useRef<HTMLDivElement>(null);
  const { lpAssets } = useAssetsWithBalance();
  const { isLPAssetV1 } = useIsLPAsset();

  const isNotV3Version = (pool: PoolFragmentFragment) => pool.version !== EContractVersion.V3;

  const isSelectedPool = (pool: PoolFragmentFragment) => {
    if (!displaySelectPoolsOnly) return true;
    const selectedPoolsIdent = selectedPools.map(({ V1 }) => V1.ident);
    return selectedPoolsIdent.includes(pool.ident);
  };

  const poolsIdent = useMemo(
    () =>
      lpAssets.reduce((acc: string[], { metadata }) => {
        if (isLPAssetV1(metadata.policyId)) {
          const assetSuffix = metadata.assetId?.slice(-2) || "";
          acc.push(assetSuffix);
        }
        return acc;
      }, []),
    [lpAssets]
  );

  const { data: pools } = usePoolsByIdent(poolsIdent);

  return (
    <div>
      <ScrollArea primitiveProps={{ viewport: { className: "max-h-[400px]" } }} className="w-full">
        <div
          ref={migrationListRef}
          className={cx("flex w-full flex-col gap-2", {
            "mx-auto max-w-[90%] !opacity-75": txState === ETransactionState.submitting,
            "motion-safe:animate-pulse": isLoadingV3Pools,
          })}
        >
          {isLoadingV3Pools || !pools
            ? Array.from({ length: 3 }).map((_, i) => (
                <LiquidityPoolMigrationListItemSkeleton key={i} />
              ))
            : sortBy(pools?.stats, "ident")
                .filter((pool) => isNotV3Version(pool) && isSelectedPool(pool))
                .map((pool) => (
                  <LiquidityPoolMigrationListItem
                    key={pool.ident}
                    pool={pool}
                    txState={txState}
                    v3Pools={v3PoolsByAssetId}
                  />
                ))}
        </div>
      </ScrollArea>

      {liquidityDialogs.createPool.isOpen && (
        <Suspense fallback={<LoadingComponent />}>
          <LazyCreatePoolDialog />
        </Suspense>
      )}
    </div>
  );
};

export default LiquidityPoolMigrationList;
